import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';
import { ThemeService } from '../../core/services/theme.service';
import { AppModule } from '../../core/rbac/rbac.policy';
import { roleLabel } from '../../core/rbac/role-label';
import { ChatWidgetComponent } from '../chat-widget/chat-widget.component';
import { ADMINISTRATION_NAV_ITEMS, filterAdminNavItems } from './shell-nav';
import type { AdminNavItem } from './shell-nav';

type NavIcon =
  | 'home'
  | 'bell'
  | 'folder'
  | 'list'
  | 'check'
  | 'flow'
  | 'briefcase'
  | 'gavel'
  | 'calendar'
  | 'users'
  | 'stamp'
  | 'scan'
  | 'chart'
  | 'shield'
  | 'gear'
  | 'user'
  | 'overview'
  | 'docs'
  | 'account'
  | 'sparkle'
  | 'search';

interface NavItem {
  label: string;
  path: string;
  module: AppModule;
  icon: NavIcon;
  exact?: boolean;
  /** Sub-paths under `path` that belong to a sibling item instead — excluded from this item's active state. */
  excludePrefixes?: string[];
}

interface NavGroup {
  id: string;
  title: string;
  icon: NavIcon;
  items: NavItem[];
}

const CRUMBS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/assistant': 'Ask AI',
  '/complaints': 'Complaint Management',
  '/workflow': 'Workflow Management',
  '/cases': 'Case Management',
  '/scheduling': 'Hearing Scheduling',
  '/hearing-management': 'Hearing Management',
  '/roster': 'Roster Management',
  '/resolution': 'Resolution Dashboard',
  '/notifications': 'Notifications',
  '/reports': 'Analytics & Reports',
  '/document-ocr': 'Document OCR',
  '/document-ocr/capture': 'Document OCR · Capture',
  '/document-ocr/documents': 'Document OCR · Documents',
  '/settings': 'User management',
  '/roles': 'Roles & permissions',
  '/resources': 'Resources',
  '/profile': 'Profile',
};

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, ChatWidgetComponent],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  private readonly auth = inject(AuthService);
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly themeSvc = inject(ThemeService);
  private readonly router = inject(Router);

  readonly officer = this.auth.officer;
  readonly unread = this.ops.unreadCount;
  readonly theme = this.themeSvc.theme;
  readonly canSettings = computed(() => this.rbac.canModule('settings'));
  readonly canNotifications = computed(() => this.rbac.canModule('notifications'));
  readonly isDm = computed(() => this.rbac.role() === 'dm');
  /** Lean DM-style workspace nav (DM + Super Admin). */
  readonly useWorkspaceNav = computed(() => {
    const role = this.rbac.role();
    return role === 'dm' || role === 'admin';
  });
  readonly isAdmin = computed(() => this.rbac.role() === 'admin');
  readonly showNotifs = signal(false);
  readonly showProfileMenu = signal(false);
  /** Off-canvas sidebar state — only rendered as a drawer below the mobile breakpoint. */
  readonly navOpen = signal(false);
  /** Accordion: which sidebar groups are expanded. */
  readonly openGroups = signal<Record<string, boolean>>({
    overview: true,
    grievances: true,
    hearings: true,
    documents: false,
    administration: false,
    account: false,
  });

  readonly initials = computed(() => {
    const name = this.officer()?.name ?? '';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  });

  readonly roleLabel = computed(() => roleLabel(this.officer()?.role));

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  readonly crumb = computed(() => {
    const path = this.url();
    if (path === '/complaints/search') return 'Search all cases';
    if (path.startsWith('/complaints/')) return 'Complaint Detail';
    if (path.startsWith('/document-ocr/review/')) return 'Document OCR · Review';
    return CRUMBS[path] ?? 'Workspace';
  });

  readonly recentNotifs = computed(() => this.ops.notificationList().slice(0, 5));

  /** Filtered Administration children (empty ⇒ hide the folder). */
  readonly adminNavItems = computed((): AdminNavItem[] =>
    filterAdminNavItems((module) => this.rbac.canModule(module)),
  );

  private readonly navGroupsDef: NavGroup[] = [
    {
      id: 'overview',
      title: 'Overview',
      icon: 'overview',
      items: [
        { label: 'Dashboard', path: '/dashboard', module: 'dashboard', icon: 'home', exact: true },
        { label: 'Ask AI', path: '/assistant', module: 'assistant', icon: 'sparkle' },
        { label: 'Notifications', path: '/notifications', module: 'notifications', icon: 'bell' },
      ],
    },
    {
      id: 'grievances',
      title: 'Grievances',
      icon: 'folder',
      items: [
        { label: 'Complaint Management', path: '/complaints', module: 'complaints', icon: 'list' },
        { label: 'Search all cases', path: '/complaints/search', module: 'complaints', icon: 'search' },
        { label: 'Workflow Management', path: '/workflow', module: 'workflow', icon: 'flow' },
        { label: 'Case Management', path: '/cases', module: 'cases', icon: 'briefcase' },
      ],
    },
    {
      id: 'hearings',
      title: 'Hearings',
      icon: 'gavel',
      items: [
        { label: 'Hearing Scheduling', path: '/scheduling', module: 'scheduling', icon: 'calendar' },
        { label: 'Hearing Management', path: '/hearing-management', module: 'hearingManagement', icon: 'gavel' },
        { label: 'Roster Management', path: '/roster', module: 'roster', icon: 'users' },
        { label: 'Resolution Dashboard', path: '/resolution', module: 'resolution', icon: 'stamp' },
      ],
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: 'docs',
      items: [
        { label: 'Document OCR', path: '/document-ocr', module: 'documentOcr', icon: 'scan' },
      ],
    },
    {
      id: 'administration',
      title: 'Administration',
      icon: 'shield',
      items: [...ADMINISTRATION_NAV_ITEMS],
    },
    {
      id: 'account',
      title: 'Account',
      icon: 'account',
      items: [
        { label: 'Analytics & Reports', path: '/reports', module: 'reports', icon: 'chart' },
        { label: 'Profile', path: '/profile', module: 'profile', icon: 'user' },
      ],
    },
  ];

  readonly navGroups = computed(() =>
    this.navGroupsDef
      .map((g) => ({
        id: g.id,
        title: g.title,
        icon: g.icon,
        items: g.items.filter(
          (item) =>
            this.rbac.canModule(item.module)
            && !(this.rbac.role() === 'dm' && (item.path === '/assistant' || item.path === '/profile')),
        ),
      }))
      .filter((g) => g.items.length > 0),
  );

  constructor() {
    if (this.auth.isAuthenticated()) {
      void this.ops.refresh().catch(() => undefined);
    }
    // Covers every way the session can end — the logout button, a 401 handled by
    // the interceptor, another tab clearing storage — so the signed-in layout is
    // never left on screen. replaceUrl keeps Back off the authenticated page.
    // Intentional logout (and other silent clears) land on the public home page;
    // the interceptor still wins for expired sessions and sends /login?reason=session.
    effect(() => {
      if (this.auth.isAuthenticated()) return;
      // A redirect already in flight (the interceptor's, which carries the
      // session-expired reason) wins; this only covers the cases nothing else did.
      if (this.router.getCurrentNavigation()) return;
      void this.router.navigateByUrl('/', { replaceUrl: true });
    });
    // Navigating from the drawer must dismiss it, or the new page stays behind the overlay.
    effect(() => {
      this.url();
      this.navOpen.set(false);
    });
    effect(() => {
      document.body.classList.toggle('nav-locked', this.navOpen());
    });
    inject(DestroyRef).onDestroy(() => document.body.classList.remove('nav-locked'));
    // Keep the group that owns the active route expanded (incl. workspace Administration).
    effect(() => {
      const groups = this.navGroups();
      for (const g of groups) {
        if (this.groupHasActive(g)) {
          this.openGroups.update((m) => ({ ...m, [g.id]: true }));
        }
      }
      if (this.adminNavItems().some((item) => this.isNavItemActive(item))) {
        this.openGroups.update((m) => ({ ...m, administration: true }));
      }
    });
  }

  isGroupOpen(id: string): boolean {
    return !!this.openGroups()[id];
  }

  isNavItemActive(item: NavItem): boolean {
    const path = this.url();
    if (item.exact) return path === item.path;
    if (path === item.path) return true;
    if (!path.startsWith(`${item.path}/`)) return false;
    return !(item.excludePrefixes || []).some((p) => path === p || path.startsWith(`${p}/`));
  }

  toggleGroup(id: string): void {
    this.openGroups.update((m) => ({ ...m, [id]: !m[id] }));
  }

  groupHasActive(group: { items: NavItem[] }): boolean {
    const path = this.url();
    return group.items.some((item) =>
      item.exact ? path === item.path : path === item.path || path.startsWith(`${item.path}/`),
    );
  }

  toggleNav(): void {
    this.navOpen.update((v) => !v);
    this.showNotifs.set(false);
    this.showProfileMenu.set(false);
  }

  closeNav(): void {
    this.navOpen.set(false);
  }

  toggleNotifs(): void {
    this.showNotifs.update((v) => !v);
    this.showProfileMenu.set(false);
  }

  toggleTheme(): void {
    this.themeSvc.toggle();
  }

  toggleProfile(): void {
    this.showProfileMenu.update((v) => !v);
    this.showNotifs.set(false);
  }

  openNotificationsPage(): void {
    this.showNotifs.set(false);
    void this.router.navigateByUrl('/notifications');
  }

  logout(): void {
    this.showProfileMenu.set(false);
    this.navOpen.set(false);
    // Navigation is left to the effect above, which fires the moment the local
    // session is dropped rather than after the revocation round-trip.
    void this.auth.logout();
  }
}
